import React, { useState, useEffect, useCallback, useRef } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

function formatCurrency(value) {
  if (!value) return '—';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function formatNumber(value) {
  if (!value) return '—';
  return value.toLocaleString();
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#3b82f6', icon: '🆕' },
  { value: 'qualified', label: 'Qualified', color: '#8b5cf6', icon: '✓' },
  { value: 'contacted', label: 'Contacted', color: '#f59e0b', icon: '📞' },
  { value: 'interested', label: 'Interested', color: '#22c55e', icon: '👍' },
  { value: 'converted', label: 'Converted', color: '#10b981', icon: '🎉' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444', icon: '✗' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#9ca3af' },
  { value: 'medium', label: 'Med', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'hot', label: 'Hot', color: '#ef4444' }
];

// Damage/Tag Templates
const TAG_OPTIONS = [
  { value: 'tornado', label: 'Tornado', icon: '🌪️', color: '#7c3aed' },
  { value: 'storm', label: 'Storm', icon: '⛈️', color: '#2563eb' },
  { value: 'hail', label: 'Hail', icon: '🧊', color: '#06b6d4' },
  { value: 'missing_shingles', label: 'Missing Shingles', icon: '🏚️', color: '#dc2626' },
  { value: 'old_roof', label: 'Old Roof', icon: '📅', color: '#ea580c' },
  { value: 'leak', label: 'Leak', icon: '💧', color: '#0891b2' }
];

function CRMLeadsPanel({ isVisible, onClose, userId, onGenerateFlyer }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [updating, setUpdating] = useState(false);
  const [lookingUp, setLookingUp] = useState(null);
  const [stats, setStats] = useState(null);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  // Fetch leads from CRM
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const teamId = 'default';
      const response = await fetch(
        `${PROPERTY_API_URL}/leads?teamId=${teamId}&limit=500`,
        { headers: { 'X-API-Key': PROPERTY_API_KEY } }
      );
      const data = await response.json();
      if (data.leads) {
        setLeads(data.leads);
        const total = data.leads.length;
        const enriched = data.leads.filter(l => l.enriched).length;
        const hot = data.leads.filter(l => l.priority === 'hot').length;
        const contacted = data.leads.filter(l => l.status === 'contacted' || l.status === 'interested').length;
        const converted = data.leads.filter(l => l.status === 'converted').length;
        setStats({ total, enriched, hot, contacted, converted });
      }
    } catch (e) {
      console.error('Failed to fetch leads:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isVisible) {
      fetchLeads();
    }
  }, [isVisible, fetchLeads]);

  // Update lead in database
  const updateLead = async (leadId, updates) => {
    setUpdating(true);
    try {
      const response = await fetch(
        `${PROPERTY_API_URL}/leads/${leadId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': PROPERTY_API_KEY
          },
          body: JSON.stringify({ ...updates, userId })
        }
      );
      if (response.ok) {
        setLeads(prev => prev.map(l =>
          l.id === leadId ? { ...l, ...updates } : l
        ));
        if (selectedLead?.id === leadId) {
          setSelectedLead(prev => ({ ...prev, ...updates }));
        }
      }
    } catch (e) {
      console.error('Failed to update lead:', e);
    }
    setUpdating(false);
  };

  // Batch update leads
  const batchUpdateLeads = async (updates) => {
    if (selectedLeadIds.size === 0) return;
    setUpdating(true);
    try {
      for (const leadId of selectedLeadIds) {
        await fetch(
          `${PROPERTY_API_URL}/leads/${leadId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': PROPERTY_API_KEY
            },
            body: JSON.stringify({ ...updates, userId })
          }
        );
      }
      setLeads(prev => prev.map(l =>
        selectedLeadIds.has(l.id) ? { ...l, ...updates } : l
      ));
      setSelectedLeadIds(new Set());
    } catch (e) {
      console.error('Failed to batch update:', e);
    }
    setUpdating(false);
  };

  // Lookup property details
  const lookupLead = async (lead) => {
    setLookingUp(lead.id);
    try {
      const address = `${lead.street_address}, ${lead.city}, ${lead.state} ${lead.zip_code}`;
      const response = await fetch(
        `${PROPERTY_API_URL}/lookup?address=${encodeURIComponent(address)}&force=true&solar=true`,
        { headers: { 'X-API-Key': PROPERTY_API_KEY } }
      );

      const data = await response.json();
      if (data.success) {
        const updates = {
          enriched: 1,
          owner_name: data.owner?.names?.join(', ') || lead.owner_name,
          sqft: data.property?.squareFootage || lead.sqft,
          year_built: data.property?.yearBuilt || lead.year_built,
          bedrooms: data.property?.bedrooms,
          bathrooms: data.property?.bathrooms,
          lot_size: data.property?.lotSize,
          assessed_value: data.valuation?.assessedValue || lead.assessed_value,
          roof_sqft: data.roofEstimate?.roofSquareFootage,
          roof_age: data.roofEstimate?.roofAge,
          roof_type: data.features?.roofType,
          roof_needs_replacement: data.roofEstimate?.needsReplacement ? 1 : 0,
          roof_cost_low: data.roofEstimate?.costEstimates?.asphalt?.estimate,
          roof_cost_high: data.roofEstimate?.costEstimates?.metal?.estimate,
          roof_sqft_measured: data.roofEstimate?.roofSquareFootageMeasured ? 1 : 0,
          roof_pitch_degrees: data.roofEstimate?.avgPitchDegrees,
          roof_segment_count: data.roofEstimate?.segmentCount
        };

        await updateLead(lead.id, updates);
        setLeads(prev => prev.map(l =>
          l.id === lead.id ? { ...l, ...updates } : l
        ));
        if (selectedLead?.id === lead.id) {
          setSelectedLead(prev => ({ ...prev, ...updates }));
        }
        setStats(prev => prev ? { ...prev, enriched: prev.enriched + 1 } : prev);
      } else {
        alert(`Lookup failed: ${data.error || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Lookup failed:', e);
      alert(`Lookup failed: ${e.message}`);
    }
    setLookingUp(null);
  };

  // Handle photo upload
  const handlePhotoUpload = async (e, leadId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(leadId);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        // Save photo URL to lead
        await updateLead(leadId, { photo_url: base64 });
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error('Photo upload failed:', e);
    }
    setUploadingPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  // Select all filtered leads
  const selectAllFiltered = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map(l => l.id)));
    }
  };

  // Print flyers for selected leads with specific tag
  const printFlyersForSelected = () => {
    const selectedData = leads.filter(l => selectedLeadIds.has(l.id));
    if (selectedData.length === 0) {
      alert('Select leads first');
      return;
    }
    if (onGenerateFlyer) {
      onGenerateFlyer(selectedData, tagFilter !== 'all' ? tagFilter : null);
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    // Status filter
    if (filter !== 'all') {
      if (filter === 'enriched' && !lead.enriched) return false;
      if (filter === 'not_enriched' && lead.enriched) return false;
      if (filter === 'hot' && lead.priority !== 'hot') return false;
      if (filter === 'needs_followup' && lead.status !== 'contacted') return false;
      if (!['all', 'enriched', 'not_enriched', 'hot', 'needs_followup'].includes(filter)) {
        if (lead.status !== filter) return false;
      }
    }
    // Tag filter
    if (tagFilter !== 'all' && lead.tag !== tagFilter) return false;
    // Search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        lead.street_address?.toLowerCase().includes(search) ||
        lead.owner_name?.toLowerCase().includes(search) ||
        lead.city?.toLowerCase().includes(search) ||
        lead.zip_code?.includes(search)
      );
    }
    return true;
  });

  if (!isVisible) return null;

  return (
    <div className="crm-overlay" onClick={onClose}>
      <div className="crm-panel crm-mobile" onClick={e => e.stopPropagation()}>
        <button className="crm-close" onClick={onClose}>&times;</button>

        <div className="crm-header">
          <h2>CRM Leads</h2>
          <p className="crm-subtitle">Manage leads and track follow-ups</p>
        </div>

        {/* Stats Bar - Compact for mobile */}
        {stats && (
          <div className="crm-stats">
            <div className="crm-stat"><span className="stat-number">{stats.total}</span><span className="stat-label">Total</span></div>
            <div className="crm-stat"><span className="stat-number" style={{ color: '#8b5cf6' }}>{stats.enriched}</span><span className="stat-label">Enriched</span></div>
            <div className="crm-stat"><span className="stat-number" style={{ color: '#ef4444' }}>{stats.hot}</span><span className="stat-label">Hot</span></div>
            <div className="crm-stat"><span className="stat-number" style={{ color: '#22c55e' }}>{stats.converted}</span><span className="stat-label">Won</span></div>
          </div>
        )}

        {/* Search */}
        <div className="crm-search">
          <input
            type="text"
            placeholder="Search address, owner, zip..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div className="crm-filter-tabs">
          {['all', 'hot', 'not_enriched', 'contacted', 'new'].map(f => (
            <button
              key={f}
              className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'not_enriched' ? 'Needs Lookup' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Tag Filter */}
        <div className="crm-tag-filter">
          <span className="tag-filter-label">Tag:</span>
          <div className="tag-filter-options">
            <button
              className={`tag-filter-btn ${tagFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTagFilter('all')}
            >
              All
            </button>
            {TAG_OPTIONS.map(tag => (
              <button
                key={tag.value}
                className={`tag-filter-btn ${tagFilter === tag.value ? 'active' : ''}`}
                style={{ '--tag-color': tag.color }}
                onClick={() => setTagFilter(tag.value)}
              >
                {tag.icon}
              </button>
            ))}
          </div>
        </div>

        {/* Batch Actions Bar */}
        {selectedLeadIds.size > 0 && (
          <div className="crm-batch-bar">
            <span className="batch-count">{selectedLeadIds.size} selected</span>
            <div className="batch-actions">
              <button onClick={() => setShowBatchActions(!showBatchActions)} className="batch-toggle">
                Actions ▾
              </button>
              <button onClick={printFlyersForSelected} className="batch-btn print">
                🖨️ Print
              </button>
              <button onClick={() => setSelectedLeadIds(new Set())} className="batch-btn clear">
                ✕
              </button>
            </div>
            {showBatchActions && (
              <div className="batch-dropdown">
                <div className="batch-section">
                  <span className="batch-section-label">Set Tag:</span>
                  <div className="batch-tag-btns">
                    {TAG_OPTIONS.map(tag => (
                      <button
                        key={tag.value}
                        onClick={() => { batchUpdateLeads({ tag: tag.value }); setShowBatchActions(false); }}
                        style={{ background: tag.color }}
                      >
                        {tag.icon} {tag.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="batch-section">
                  <span className="batch-section-label">Set Priority:</span>
                  <div className="batch-priority-btns">
                    {PRIORITY_OPTIONS.map(p => (
                      <button
                        key={p.value}
                        onClick={() => { batchUpdateLeads({ priority: p.value }); setShowBatchActions(false); }}
                        style={{ background: p.color }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Leads List */}
        <div className="crm-leads-list">
          {/* Select All */}
          <div className="select-all-row">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectedLeadIds.size === filteredLeads.length && filteredLeads.length > 0}
                onChange={selectAllFiltered}
              />
              Select All ({filteredLeads.length})
            </label>
          </div>

          {loading ? (
            <div className="crm-loading"><span className="spinner"></span>Loading...</div>
          ) : filteredLeads.length === 0 ? (
            <div className="crm-empty">
              <div className="empty-icon">📋</div>
              <h3>No leads found</h3>
              <p>Save leads from the map to see them here</p>
            </div>
          ) : (
            filteredLeads.map(lead => (
              <div
                key={lead.id}
                className={`crm-lead-card ${selectedLead?.id === lead.id ? 'expanded' : ''} ${lead.priority === 'hot' ? 'hot' : ''}`}
              >
                {/* Card Header - Always Visible */}
                <div className="card-header" onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}>
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.has(lead.id)}
                    onChange={(e) => { e.stopPropagation(); toggleLeadSelection(lead.id); }}
                    className="lead-checkbox"
                  />
                  <div className="card-main">
                    <div className="card-address">{lead.street_address}</div>
                    <div className="card-city">{lead.city}, {lead.state} {lead.zip_code}</div>
                  </div>
                  <div className="card-badges">
                    {lead.tag && (
                      <span className="tag-badge" style={{ background: TAG_OPTIONS.find(t => t.value === lead.tag)?.color }}>
                        {TAG_OPTIONS.find(t => t.value === lead.tag)?.icon}
                      </span>
                    )}
                    {lead.enriched ? <span className="badge enriched">✓</span> : null}
                    {lead.priority === 'hot' && <span className="badge hot">HOT</span>}
                  </div>
                </div>

                {/* Quick Info Row */}
                <div className="card-quick-info">
                  {lead.owner_name && <span className="owner-name">👤 {lead.owner_name}</span>}
                  {lead.roof_cost_low && (
                    <span className="roof-est">
                      {lead.roof_needs_replacement ? '⚠️' : '🏠'} {formatCurrency(lead.roof_cost_low)}
                    </span>
                  )}
                </div>

                {/* Quick Action Buttons - Always Visible */}
                <div className="card-quick-actions">
                  {/* Tag Buttons */}
                  <div className="quick-tags">
                    {TAG_OPTIONS.slice(0, 4).map(tag => (
                      <button
                        key={tag.value}
                        className={`quick-tag-btn ${lead.tag === tag.value ? 'active' : ''}`}
                        style={{ '--tag-color': tag.color }}
                        onClick={(e) => { e.stopPropagation(); updateLead(lead.id, { tag: tag.value }); }}
                        title={tag.label}
                      >
                        {tag.icon}
                      </button>
                    ))}
                  </div>

                  {/* Status Quick Select */}
                  <select
                    className="quick-status"
                    value={lead.status || 'new'}
                    onChange={(e) => { e.stopPropagation(); updateLead(lead.id, { status: e.target.value }); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ borderColor: STATUS_OPTIONS.find(s => s.value === lead.status)?.color }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.icon} {s.label}</option>
                    ))}
                  </select>

                  {/* Priority Quick Select */}
                  <select
                    className="quick-priority"
                    value={lead.priority || 'medium'}
                    onChange={(e) => { e.stopPropagation(); updateLead(lead.id, { priority: e.target.value }); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ borderColor: PRIORITY_OPTIONS.find(p => p.value === lead.priority)?.color }}
                  >
                    {PRIORITY_OPTIONS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>

                  {/* Lookup Button */}
                  <button
                    className={`quick-lookup-btn ${lookingUp === lead.id ? 'loading' : ''}`}
                    onClick={(e) => { e.stopPropagation(); lookupLead(lead); }}
                    disabled={lookingUp === lead.id}
                    title={lead.enriched ? 'Re-lookup' : 'Lookup'}
                  >
                    {lookingUp === lead.id ? '...' : lead.enriched ? '🔄' : '🔍'}
                  </button>
                </div>

                {/* Expanded Details */}
                {selectedLead?.id === lead.id && (
                  <div className="card-expanded">
                    {/* Photo Section */}
                    <div className="photo-section">
                      {lead.photo_url ? (
                        <div className="lead-photo">
                          <img src={lead.photo_url} alt="Property" />
                          <button
                            className="change-photo-btn"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Change Photo
                          </button>
                        </div>
                      ) : (
                        <button
                          className="add-photo-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingPhoto === lead.id}
                        >
                          {uploadingPhoto === lead.id ? 'Uploading...' : '📷 Add Photo'}
                        </button>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handlePhotoUpload(e, lead.id)}
                      />
                    </div>

                    {/* Property Details */}
                    {lead.enriched && (
                      <div className="detail-grid">
                        {lead.sqft && <div className="detail-item"><span>📐</span>{formatNumber(lead.sqft)} sqft</div>}
                        {lead.year_built && <div className="detail-item"><span>🏠</span>Built {lead.year_built}</div>}
                        {lead.bedrooms && <div className="detail-item"><span>🛏️</span>{lead.bedrooms} bed</div>}
                        {lead.bathrooms && <div className="detail-item"><span>🚿</span>{lead.bathrooms} bath</div>}
                        {lead.roof_sqft && <div className="detail-item"><span>🔺</span>{formatNumber(lead.roof_sqft)} sqft roof</div>}
                        {lead.roof_age && <div className="detail-item"><span>📅</span>{lead.roof_age}yr roof</div>}
                      </div>
                    )}

                    {/* Roof Estimate */}
                    {lead.roof_cost_low && (
                      <div className="roof-estimate-box">
                        <div className="estimate-title">
                          {lead.roof_needs_replacement ? '⚠️ Needs Replacement' : 'Roof Estimate'}
                        </div>
                        <div className="estimate-range">
                          {formatCurrency(lead.roof_cost_low)} - {formatCurrency(lead.roof_cost_high)}
                        </div>
                      </div>
                    )}

                    {/* All Tags */}
                    <div className="all-tags-section">
                      <span className="section-label">Damage Type:</span>
                      <div className="all-tags">
                        {TAG_OPTIONS.map(tag => (
                          <button
                            key={tag.value}
                            className={`tag-btn ${lead.tag === tag.value ? 'active' : ''}`}
                            style={{ '--tag-color': tag.color }}
                            onClick={() => updateLead(lead.id, { tag: lead.tag === tag.value ? null : tag.value })}
                          >
                            {tag.icon} {tag.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="expanded-actions">
                      <button
                        className="action-btn flyer"
                        onClick={() => onGenerateFlyer && onGenerateFlyer([lead], lead.tag)}
                      >
                        📄 Generate Flyer
                      </button>
                      <button
                        className="action-btn copy"
                        onClick={() => {
                          const addr = `${lead.street_address}, ${lead.city}, ${lead.state} ${lead.zip_code}`;
                          navigator.clipboard.writeText(addr);
                        }}
                      >
                        📋 Copy Address
                      </button>
                    </div>

                    <div className="lead-meta-footer">
                      Added {formatDate(lead.created_at)}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Bottom Actions */}
        <div className="crm-bottom">
          <button className="refresh-btn" onClick={fetchLeads} disabled={loading}>
            🔄 Refresh
          </button>
          <span className="leads-count">{filteredLeads.length} leads</span>
        </div>
      </div>
    </div>
  );
}

export default CRMLeadsPanel;
